import { useState, useMemo, useCallback } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import FinanceXOnboardingContainer from '@/pages/FinanceXOnboarding/FinanceXOnboarding.container';
import financeCategoriesService from '@/services/financeCategories';
import financeClientsService, {
  MonthlyClientBreakdown,
  DrillDownRow,
  CogsDrillDownRow,
  RevenueForecastDrillDownRow,
} from '@/services/financeClients';
import financePlaidService from '@/services/financePlaid';
import financeQuickBooksService, {
  isQbConnectionStale,
  QbConnection,
} from '@/services/financeQuickBooks';
import financeTransactionsService from '@/services/financeTransactions';
import { FetchApiIds } from '@/types';
import { FxBankConnection, FxCategory } from '@/types/api/finance';
import FinanceXDashboardComponent from './FinanceXDashboard.component';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

function lastDayOfMonth(year: number, month: number): string {
  const d = new Date(year, month + 1, 0);
  return d.toISOString().split('T')[0];
}

function getYearRange(year: number): { startDate: string; endDate: string } {
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return {
    startDate: target.toISOString().split('T')[0],
    endDate: lastDayOfMonth(target.getFullYear(), target.getMonth()),
  };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const endMonth = q * 3 + 2;
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: lastDayOfMonth(now.getFullYear(), endMonth),
  };
}

const DEFAULT_BREAKDOWN: MonthlyClientBreakdown = {
  months: [], forecastStartMonth: null, totalRevenue: {}, totalCogs: {}, totalActualCogs: {}, totalOpsPlan: {}, totalOpsActual: {}, totalOpex: {}, clients: [],
};

const FinanceXDashboardContainer = function (): JSX.Element {
  const now = new Date();
  const currentYear = now.getFullYear();
  const todayMonth = `${currentYear}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const orgId = getActiveOrgId();

  // --- All hooks must be called unconditionally (React rules of hooks) ---

  // Onboarding check: bank connections + transaction existence
  const { data: connections } = useSeoFetch<FxBankConnection[]>(
    [FetchApiIds.FX_BANK_CONNECTIONS, 'onboarding-check', orgId],
    () => financePlaidService.getConnections(),
    { retry: false }
  );

  // QuickBooks connections, for the stale-sync banner below.
  const { data: qbConnections } = useSeoFetch<QbConnection[]>(
    [FetchApiIds.FX_QB_CONNECTIONS, orgId],
    () => financeQuickBooksService.getConnections(),
    { retry: false }
  );

  const { data: hasData } = useSeoFetch<boolean>(
    ['fx-has-any-data', orgId],
    () => financeTransactionsService.hasAnyData(),
    { retry: false }
  );

  // Dashboard state
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_year',
    ...getYearRange(currentYear),
  });

  const handlePresetChange = useCallback(
    (preset: DatePreset) => {
      switch (preset) {
        case 'this_month':
          setDateFilter({ preset, ...getMonthRange(0) });
          break;
        case 'last_month':
          setDateFilter({ preset, ...getMonthRange(-1) });
          break;
        case 'this_quarter':
          setDateFilter({ preset, ...getQuarterRange() });
          break;
        case 'this_year':
          setDateFilter({ preset, ...getYearRange(currentYear) });
          break;
        case 'last_year':
          setDateFilter({ preset, ...getYearRange(currentYear - 1) });
          break;
        default:
          setDateFilter((prev) => ({ ...prev, preset }));
      }
    },
    [currentYear]
  );

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  // Show onboarding when org has no bank connections and no transactions
  const showOnboarding =
    connections !== undefined &&
    hasData !== undefined &&
    connections.length === 0 &&
    !hasData;

  // Period totals
  const totalsResponse = useApiFetch<{ revenue: number; expenses: number }, void>(
    ['fx-totals', orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getTotalsByType({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  // Monthly breakdown
  const monthlyResponse = useApiFetch<
    Array<{ month: string; revenue: number; expenses: number }>,
    void
  >(
    ['fx-monthly-totals', orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getMonthlyTotals({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  // Spend by category
  const spendByCatResponse = useApiFetch<
    Array<{ categoryId: string; categoryName: string; total: number }>,
    void
  >(
    [FetchApiIds.FX_SPEND_BY_CATEGORY, orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getSpendByCategory({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  // Top vendors
  const topVendorsResponse = useApiFetch<
    Array<{ vendorId: string; vendorName: string; total: number; txCount: number }>,
    void
  >(
    ['fx-top-vendors', orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getTopVendors({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
        limit: 10,
      }),
    { retry: false }
  );

  // Monthly net burn
  const burnResponse = useApiFetch<
    Array<{ month: string; burn: number; cumulative: number }>,
    void
  >(
    ['fx-monthly-burn', orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getMonthlyBurn({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  // Uncategorized count
  const uncatResponse = useApiFetch<number, void>(
    ['fx-uncategorized', orgId, dateFilter.startDate, dateFilter.endDate],
    async () =>
      financeTransactionsService.getUncategorizedCount({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  // Monthly client revenue breakdown (fixed 12-month window, independent of date filter)
  const clientRevenueResponse = useApiFetch<MonthlyClientBreakdown, void>(
    ['fx-monthly-client-revenue', orgId],
    async () => {
      const result = await financeClientsService.getMonthlyClientRevenue();
      return { data: result } as any;
    },
    { retry: false }
  );

  // Categories
  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const totals = useMemo(
    () =>
      (totalsResponse.data as { revenue: number; expenses: number }) ?? { revenue: 0, expenses: 0 },
    [totalsResponse.data]
  );

  const monthlyTotals = useMemo(
    () =>
      (monthlyResponse.data as Array<{ month: string; revenue: number; expenses: number }>) ?? [],
    [monthlyResponse.data]
  );

  const spendByCategory = useMemo(
    () =>
      (spendByCatResponse.data as Array<{
        categoryId: string;
        categoryName: string;
        total: number;
      }>) ?? [],
    [spendByCatResponse.data]
  );

  const topVendors = useMemo(
    () =>
      (topVendorsResponse.data as Array<{
        vendorId: string;
        vendorName: string;
        total: number;
        txCount: number;
      }>) ?? [],
    [topVendorsResponse.data]
  );

  const monthlyBurn = useMemo(
    () => (burnResponse.data as Array<{ month: string; burn: number; cumulative: number }>) ?? [],
    [burnResponse.data]
  );

  const uncategorizedCount = useMemo(
    () => (uncatResponse.data as number) ?? 0,
    [uncatResponse.data]
  );

  const categories = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const monthlyClientRevenue = useMemo(
    (): MonthlyClientBreakdown => {
      const raw = clientRevenueResponse.data;
      const d = (raw as any)?.data ?? raw;
      return (d as MonthlyClientBreakdown) ?? DEFAULT_BREAKDOWN;
    },
    [clientRevenueResponse.data]
  );

  const categoryColorMap = useMemo(
    () => new Map(categories.map((c: FxCategory) => [c.name, c.color ?? '#6B7280'])),
    [categories]
  );

  // Bank connections that Plaid has invalidated and need re-authentication.
  const reauthConnections = useMemo(
    () =>
      (connections ?? []).filter(
        (c) => c.status === 'disconnected' || (c.settings as any)?.needs_reauth === true
      ),
    [connections]
  );

  // QuickBooks connections that have stopped syncing. Keyed on staleness rather
  // than status, which stayed 'active' through a 20-day outage.
  const staleQbConnections = useMemo(
    () => (qbConnections ?? []).filter(isQbConnectionStale),
    [qbConnections]
  );

  // Compute net income and avg monthly burn
  const netIncome = totals.revenue - totals.expenses;
  const avgMonthlyBurn =
    monthlyBurn.length > 0
      ? Math.round(monthlyBurn.reduce((sum, m) => sum + m.burn, 0) / monthlyBurn.length)
      : 0;

  const { isLoading } = totalsResponse;

  // Drill-down state
  type DrillDownType = 'revenue' | 'cogs' | 'ops' | 'opex';
  const [drillDown, setDrillDown] = useState<{
    open: boolean;
    type: DrillDownType;
    month: string;
    label: string;
    rows: DrillDownRow[];
    cogsRows: CogsDrillDownRow[];
    forecastRows: RevenueForecastDrillDownRow[];
    loading: boolean;
  }>({ open: false, type: 'revenue', month: '', label: '', rows: [], cogsRows: [], forecastRows: [], loading: false });

  const handleDrillDown = useCallback(async (type: DrillDownType, month: string, label: string) => {
    setDrillDown({ open: true, type, month, label, rows: [], cogsRows: [], forecastRows: [], loading: true });
    const isFuture = month > todayMonth;
    try {
      if (type === 'revenue' && isFuture) {
        const forecastRows = await financeClientsService.getRevenueForecastDrillDown(month);
        setDrillDown((prev) => ({ ...prev, forecastRows, loading: false }));
      } else if (type === 'revenue') {
        const rows = await financeClientsService.getRevenueDrillDown(month);
        setDrillDown((prev) => ({ ...prev, rows, loading: false }));
      } else if (type === 'cogs') {
        const cogsRows = await financeClientsService.getCogsDrillDown(month);
        setDrillDown((prev) => ({ ...prev, cogsRows, loading: false }));
      } else if (type === 'ops') {
        const cogsRows = await financeClientsService.getOpsPeopleDrillDown(month);
        setDrillDown((prev) => ({ ...prev, cogsRows, loading: false }));
      } else {
        const rows = await financeClientsService.getOpexDrillDown(month);
        setDrillDown((prev) => ({ ...prev, rows, loading: false }));
      }
    } catch {
      setDrillDown((prev) => ({ ...prev, loading: false }));
    }
  }, [todayMonth]);

  const handleCloseDrillDown = useCallback(() => {
    setDrillDown((prev) => ({ ...prev, open: false }));
  }, []);

  // --- Conditional render AFTER all hooks ---

  if (showOnboarding) {
    return <FinanceXOnboardingContainer />;
  }

  return (
    <FinanceXDashboardComponent
      revenue={totals.revenue}
      expenses={totals.expenses}
      netIncome={netIncome}
      avgMonthlyBurn={avgMonthlyBurn}
      uncategorizedCount={uncategorizedCount}
      spendByCategory={spendByCategory}
      topVendors={topVendors}
      monthlyTotals={monthlyTotals}
      monthlyBurn={monthlyBurn}
      categoryColorMap={categoryColorMap}
      monthlyClientRevenue={monthlyClientRevenue}
      reauthConnections={reauthConnections}
      staleQbConnections={staleQbConnections}
      isLoading={isLoading}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
      drillDown={drillDown}
      onDrillDown={handleDrillDown}
      onCloseDrillDown={handleCloseDrillDown}
    />
  );
};

export default FinanceXDashboardContainer;
